import { useRef, useState } from "react";

/**
 * Pinch-zoom + 2-finger pan + desktop wheel-zoom + middle/right-button
 * pan handlers for an SVG canvas.
 *
 * Keeps the SVG's viewBox intact (so all pointer-to-world math works
 * unchanged) and applies the transform to a wrapper <div>. Single
 * left-click touches pass through to existing pointer handlers.
 *
 * Returns:
 *   touchZoom            : { scale, tx, ty }
 *   onTouchStart/Move/End: pinch + 2-finger pan
 *   onWheel              : ctrl+wheel / trackpad pinch zooms at cursor
 *   onMouseDownPan       : middle / right mouse button drag-to-pan
 *   reset                : back to 1x / 0 / 0
 *
 * V2.0.61 — Added desktop wheel-zoom + middle/right drag-pan so the
 * floor plan can be moved + zoomed without touching the surrounding
 * tool palette.
 */
export default function useTouchZoom() {
  const [touchZoom, setTouchZoom] = useState({ scale: 1, tx: 0, ty: 0 });
  const pinchRef = useRef(null);
  const panRef = useRef(null);

  const onTouchStart = (e) => {
    if (e.touches && e.touches.length === 2) {
      const t0 = e.touches[0]; const t1 = e.touches[1];
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const cx = (t0.clientX + t1.clientX) / 2;
      const cy = (t0.clientY + t1.clientY) / 2;
      pinchRef.current = {
        dist0: dist, cx0: cx, cy0: cy,
        scale0: touchZoom.scale, tx0: touchZoom.tx, ty0: touchZoom.ty,
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches && e.touches.length === 2 && pinchRef.current) {
      if (e.cancelable) e.preventDefault();
      const t0 = e.touches[0]; const t1 = e.touches[1];
      const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY);
      const cx = (t0.clientX + t1.clientX) / 2;
      const cy = (t0.clientY + t1.clientY) / 2;
      const p = pinchRef.current;
      const s = Math.max(0.4, Math.min(5, p.scale0 * (dist / p.dist0)));
      const tx = p.tx0 + (cx - p.cx0);
      const ty = p.ty0 + (cy - p.cy0);
      setTouchZoom({ scale: s, tx, ty });
    }
  };

  const onTouchEnd = (e) => {
    if (!e.touches || e.touches.length < 2) pinchRef.current = null;
  };

  // V2.0.61 — Desktop wheel zoom. Only fires when the user is hovering
  // the floor plan AND scrolls. Zooms toward the cursor so the point
  // under the mouse stays fixed.
  const onWheel = (e) => {
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && Math.abs(e.deltaY) < 4) return;
    // Trackpad pinch fires `wheel` with ctrlKey=true. Mouse wheel with
    // ctrl held also zooms; without ctrl we ignore so the page can scroll.
    if (!e.ctrlKey && !e.metaKey) return;
    if (e.cancelable) e.preventDefault();
    const target = e.currentTarget?.getBoundingClientRect?.();
    if (!target) return;
    const px = e.clientX - target.left;
    const py = e.clientY - target.top;
    setTouchZoom((tz) => {
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const nextScale = Math.max(0.4, Math.min(5, tz.scale * factor));
      // Anchor the zoom at the cursor: keep (px,py) at the same place
      // before and after the scale change.
      const k = nextScale / tz.scale;
      const nextTx = px - k * (px - tz.tx);
      const nextTy = py - k * (py - tz.ty);
      return { scale: nextScale, tx: nextTx, ty: nextTy };
    });
  };

  // V2.0.61 — Middle / right mouse button drag-to-pan. Left-click still
  // routes to the existing pointer handlers (drawing walls, placing
  // symbols, dragging wall handles).
  const onMouseDownPan = (e) => {
    if (e.button !== 1 && e.button !== 2) return;
    if (e.cancelable) e.preventDefault();
    panRef.current = {
      startX: e.clientX, startY: e.clientY,
      tx0: touchZoom.tx, ty0: touchZoom.ty,
    };
    const handleMove = (ev) => {
      const p = panRef.current;
      if (!p) return;
      setTouchZoom((tz) => ({
        ...tz,
        tx: p.tx0 + (ev.clientX - p.startX),
        ty: p.ty0 + (ev.clientY - p.startY),
      }));
    };
    const handleUp = () => {
      panRef.current = null;
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  };

  const reset = () => setTouchZoom({ scale: 1, tx: 0, ty: 0 });

  return { touchZoom, onTouchStart, onTouchMove, onTouchEnd, onWheel, onMouseDownPan, reset };
}
